import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Edit3, Check, X } from 'lucide-react';
import { getPositionsForSport } from '@/data/sportsPositions';
import { getCoachRolesForSport, getSupportRolesForSport, formatRoleName } from '@/data/sportRoles';

interface PositionEditorProps {
  currentValue: string;
  memberType: 'player' | 'coach' | 'support';
  sport?: string;
  onUpdate: (newValue: string) => Promise<void>;
  className?: string;
}

export const PositionEditor = ({ 
  currentValue, 
  memberType, 
  sport = 'Basketball', 
  onUpdate, 
  className = '' 
}: PositionEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentValue);
  const [isUpdating, setIsUpdating] = useState(false);

  const getOptionsForType = () => {
    switch (memberType) {
      case 'player':
        return getPositionsForSport(sport);
      case 'coach':
        return getCoachRolesForSport(sport);
      case 'support':
        return getSupportRolesForSport(sport);
      default:
        return [];
    }
  };

  const handleSave = async () => {
    if (selectedValue === currentValue) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdate(selectedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating position/role:', error);
      setSelectedValue(currentValue); // Reset on error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setSelectedValue(currentValue);
    setIsEditing(false);
  };

  const displayValue = currentValue || (memberType === 'player' ? 'Position' : 'Role');
  const isPlaceholder = !currentValue;

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Select value={selectedValue} onValueChange={setSelectedValue}>
          <SelectTrigger className="h-7 text-xs bg-background border-muted">
            <SelectValue placeholder={`Select ${memberType === 'player' ? 'position' : 'role'}`} />
          </SelectTrigger>
          <SelectContent className="bg-background border-border">
            {getOptionsForType().map((option) => (
              <SelectItem key={option} value={option} className="text-xs">
                {memberType === 'player' ? option : formatRoleName(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={isUpdating}
          className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={isUpdating}
          className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center gap-2 group ${className}`}>
      <span className={`text-xs ${isPlaceholder ? 'text-muted-foreground italic' : 'text-foreground'}`}>
        {memberType === 'player' ? displayValue : formatRoleName(displayValue)}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsEditing(true)}
        className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
      >
        <Edit3 className="h-3 w-3" />
      </Button>
    </div>
  );
};